import React, { useEffect, useState } from "react";
import LightThemePage from "../../components/layout/LightThemePage";
import { get, lowerCase } from "lodash";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import i18next, { t } from "i18next";
import { formatDate } from "../../shared/utils/DateUtils";
import Page from "./Page";
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  Typography,
} from "@mui/material";
import Header from "./Header";
import FirstTable from "./FirstTable";
import Footer from "./footer";
import SecondTable from "./SecondTable";
import { TrendingUp } from "@mui/icons-material";

function TrainingsPreview() {
  let [searchParams] = useSearchParams();

  const [screenShot, setScreenShot] = useState("");
  const [training, setTraining] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [isSlowTraining, toggleSlowTraining] = useState(false);

  let token = searchParams.get("token");
  let user_id = searchParams.get("user_id");
  let lang = searchParams.get("lang") ?? "en";
  let training_id = searchParams.get("training_id");
  let unitSystem = searchParams.get("unit-system");
  let unitSpeed = searchParams.get("speed-unit");
  let dateFormat = searchParams.get("date_format");

  const getLink = () => {
    const baseUrl = process.env.REACT_APP_API_BASE_URI;

    if (!baseUrl) {
      throw new Error(
        "REACT_APP_API_BASE_URI environment variable is not defined",
      );
    }

    if (!training_id) {
      throw new Error("Missing required training_id parameter");
    }

    // Ensure base URL ends with a slash so new URL() appends correctly
    const sanitizedBase = baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
    const url = new URL(
      `trainings/${encodeURIComponent(training_id)}/pdf_data`,
      sanitizedBase,
    );

    const params = {
      token,
      user: user_id,
      lang,
      "unit-system": unitSystem,
      "speed-unit": unitSpeed,
      date_format: dateFormat,
    };

    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        url.searchParams.set(key, value);
      }
    });

    return url.toString();
  };

  useEffect(() => {
    const currentLang = lowerCase(lang);
    i18next.changeLanguage(currentLang);

    // Set RTL direction based on language
    const isRtl = currentLang === "ar";
    document.documentElement.dir = isRtl ? "rtl" : "ltr";
    document.documentElement.lang = currentLang;

    setLoading(true);
    setError(null);
    axios
      .get(getLink())
      .then(({ data }) => {
        handleUserLangForScreenShot(data);
        setTraining(data);

        //Set if training is considered slow < 46
        toggleSlowTraining(data?.slow_training ?? false);
      })
      .catch((err) => {
        console.log({ err });
        setError(err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const handleUserLangForScreenShot = (data) => {
    if (lowerCase(lang) === lowerCase("FR")) {
      setScreenShot(get(data, "training.screenshots.pdf_figure_fr_url"));
    } else if (lowerCase(lang) === lowerCase("EN")) {
      setScreenShot(get(data, "training.screenshots.pdf_figure_en_url"));
    } else {
      setScreenShot(get(data, "training.screenshots.pdf_figure_en_url"));
    }
  };
  const getDate = () => {
    return get(training, "training.creation_date")
      ? formatDate(get(training, "training.creation_date"), "DD-MM-YYYY HH:mm")
      : "";
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.5,
          mt: 5,
        }}
      >
        <CircularProgress size={24} />
        <Typography variant="h6" fontWeight={600}>
          Chargement...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 5, px: 2 }}>
        <Alert severity="error" variant="outlined" sx={{ fontWeight: 600 }}>
          Oops une erreur est survenue
        </Alert>
      </Box>
    );
  }

  return (
    <LightThemePage>
      <div data-testid="trainings-content" data-loaded={!loading}>
        <Page
          footer={<Footer training={training} slowTraining={isSlowTraining} />}
        >
          <Header dateFormat={dateFormat} training={training} showInfo></Header>
          <Box px={1}>
            {/* Screenshot Section */}
            <img
              style={{
                height: "400px",
                width: "100%",
                objectFit: "contain",
              }}
              id="training_chart"
              alt="graph"
              src={screenShot}
            ></img>
            <FirstTable training={training} slowTraining={isSlowTraining} />
          </Box>
        </Page>

        <Page
          mode="landscape"
          footer={
            <Box
              component="span"
              display={"flex"}
              flexDirection={"row"}
              alignItems={"flex-start"}
              px={1}
              gap={1}
            >
              <TrendingUp sx={{ fontSize: ".9rem" }} color="grey" />
              <Typography fontSize={"0.6rem"}>
                {t("pdf:footer:arrowsExplanation")}
              </Typography>
            </Box>
          }
        >
          {/* Second page - only full height if NOT slow training */}
          {get(training, "past_trainings_key_parameters", []).length > 0 && (
            <>
              {!isSlowTraining && (
                <Header
                  dateFormat={dateFormat}
                  training={training}
                  showInfo={false}
                ></Header>
              )}
              <Box px={'3px'}>
                <SecondTable
                  insightItems={get(training, "insight_items", [])}
                  slowTraining={isSlowTraining}
                  parameters={get(
                    training,
                    "past_trainings_key_parameters",
                    [],
                  )}
                  dateFormat={dateFormat}
                />
              </Box>
            </>
          )}
        </Page>
      </div>
    </LightThemePage>
  );
}

export default TrainingsPreview;
